import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { getTheme, fonts } from "../theme";
import Navbar from "../components/Navbar";
import SiteFooter from "../components/SiteFooter";

const DEFAULT_TITLE = "WIBIL — Workforce Information Bureau (India) Limited";
const PAGE_TITLE = "Privacy Policy | WIBIL";
const PAGE_DESCRIPTION =
  "Privacy Policy for WIBIL, a hiring and workforce platform by GoldPE.AI.";

const SECTIONS = [
  { id: "introduction", title: "Introduction" },
  { id: "information-we-collect", title: "Information We Collect" },
  { id: "account-authentication", title: "Account and Authentication Information" },
  { id: "candidate-recruiter", title: "Candidate and Recruiter Information" },
  { id: "linkedin-integrations", title: "LinkedIn and Third-Party Integrations" },
  { id: "indeed-integration", title: "Indeed Integration" },
  { id: "how-we-use", title: "How We Use Information" },
  { id: "data-sharing", title: "Data Sharing and Disclosure" },
  { id: "data-security", title: "Data Security" },
  { id: "data-retention", title: "Data Retention" },
  { id: "data-deletion", title: "Data Deletion and User Requests" },
  { id: "cookies", title: "Cookies and Tracking Technologies" },
  { id: "third-party-services", title: "Third-Party Services" },
  { id: "user-rights", title: "User Rights" },
  { id: "children", title: "Children's Privacy" },
  { id: "changes", title: "Changes to This Privacy Policy" },
  { id: "contact", title: "Contact Us" },
];

export default function PrivacyPolicy({ onLogout, themeMode = "dark", setThemeMode }) {
  const theme = getTheme("privacy_policy", themeMode);

  useEffect(() => {
    document.title = PAGE_TITLE;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const previous = meta.getAttribute("content");
    meta.setAttribute("content", PAGE_DESCRIPTION);
    return () => {
      document.title = DEFAULT_TITLE;
      if (previous != null) meta.setAttribute("content", previous);
      else meta.removeAttribute("content");
    };
  }, []);

  return (
    <div className="min-vh-100 bg-body d-flex flex-column" style={{ fontFamily: fonts.body }}>
      <Navbar theme={theme} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />

      <main className="container py-4 py-md-5 flex-grow-1">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10 col-xl-9">
            <header className="mb-4 mb-md-5">
              <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-primary bg-opacity-10 text-primary border border-primary-subtle mb-3 small fw-bold">
                <ShieldCheck size={14} /> Legal
              </div>
              <h1 className="h2 fw-bold text-body mb-2" style={{ fontFamily: fonts.display }}>
                Privacy Policy
              </h1>
              <p className="text-secondary mb-1">
                WIBIL — Workforce Information Bureau (India) Limited, a hiring and workforce platform by GoldPE.AI
              </p>
              <p className="small text-secondary mb-0">Effective date: 13 August 2026</p>
            </header>

            <nav className="card border rounded-4 shadow-sm mb-4 p-3 p-md-4 bg-body-tertiary" aria-label="On this page">
              <h2 className="h6 fw-bold text-body mb-3" style={{ fontFamily: fonts.display }}>
                On this page
              </h2>
              <ul className="row g-2 list-unstyled mb-0 small">
                {SECTIONS.map((s) => (
                  <li key={s.id} className="col-12 col-sm-6">
                    <a href={`#${s.id}`} className="text-primary text-decoration-none">
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <article className="card border rounded-4 shadow-sm p-3 p-md-4 p-lg-5 bg-body" style={{ color: theme.text }}>
              <section id="introduction" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Introduction</h2>
                <p>
                  This Privacy Policy describes how WIBIL (“we”, “us”, or “our”) collects, uses, stores, and shares
                  information when you use https://wibil.co and related services (the “Service”).
                </p>
                <p>
                  WIBIL is an applicant tracking and workforce information platform. Depending on how you use the
                  Service, we may process information needed for recruitment, candidate matching, account
                  authentication, identity or employment verification, ratings, and integrations with authorized
                  third-party services such as LinkedIn Recruiter System Connect and Indeed Partner APIs.
                </p>
                <p className="mb-0">
                  This policy applies to employees, job seekers, employers, and recruiters who register or use WIBIL.
                  It is written to match features that exist in the current WIBIL application. It does not claim
                  certifications, encryption standards, or compliance marks that are not implemented as independently
                  audited programs in the product.
                </p>
              </section>

              <section id="information-we-collect" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Information We Collect</h2>
                <p>We collect information that you provide, information generated by your use of the Service, and information we receive from integrations you or your organization connect.</p>
                <ul className="mb-0">
                  <li>Account details such as name, email address, phone number, role, password, and company or job information you enter on registration and profile forms.</li>
                  <li>Profile content such as skills, work history, social or portfolio URLs, resume files, and profile photographs you upload.</li>
                  <li>Verification information you choose to submit, which may include GST numbers, UAN/EPFO details, mobile OTP verification, and identity document images (for example Aadhaar images) where those flows are offered.</li>
                  <li>Recruiting and hiring records such as job applications, shortlists, ratings, notes attached to exported candidates, and pipeline or requisition identifiers received from connected ATS integrations.</li>
                  <li>Technical information needed to operate the site, including authentication tokens stored in your browser and your selected theme preference.</li>
                </ul>
              </section>

              <section id="account-authentication" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Account and Authentication Information</h2>
                <p>
                  To create and sign in to an account, WIBIL collects your email address, password, and selected role
                  (employee, job seeker, employer, or recruiter). Passwords are submitted over the site’s HTTPS
                  connection and stored as hashed credentials by the authentication service. We do not display your
                  password in the interface.
                </p>
                <p>
                  Email one-time passwords (OTP) are used to verify email addresses during registration and password
                  reset. OTP messages are sent from WIBIL’s transactional mailbox. After you sign in, a session token
                  and a copy of your user profile are stored in browser session storage and local storage so the
                  application can keep you signed in.
                </p>
                <p className="mb-0">
                  Forgot-password flows replace your password only after email OTP verification for the selected role.
                </p>
              </section>

              <section id="candidate-recruiter" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Candidate and Recruiter Information</h2>
                <p>WIBIL processes candidate and recruiter information to operate hiring workflows in Opportunity Hub and related dashboards. This may include:</p>
                <ul>
                  <li>Names, contact details, job titles, skills, companies, locations, experience, and resume or profile URLs.</li>
                  <li>Job applications and hiring pipeline stages stored for recruiters and employers.</li>
                  <li>Employer and employee ratings submitted through the rating pages.</li>
                  <li>Recruiter-initiated shortlist communications, including WhatsApp shortlist messages where that feature is used.</li>
                  <li>Public professional links you add (for example LinkedIn, GitHub, Indeed, Glassdoor, or portfolio URLs) so they can be shown on your profile or used in recruiter search.</li>
                </ul>
                <p className="mb-0">
                  Some recruiter tools generate search links to third-party sites or query public APIs (for example
                  GitHub user search). WIBIL does not claim to scrape private LinkedIn or Indeed resume databases.
                </p>
              </section>

              <section id="linkedin-integrations" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>LinkedIn and Third-Party Integrations</h2>
                <p>
                  When a customer enables LinkedIn Recruiter System Connect (RSC), WIBIL may process information
                  required to authenticate with LinkedIn using OAuth 2.0 client credentials and to receive Recruiter
                  One-Click Export events.
                </p>
                <p>For LinkedIn RSC Module 3 (Retrieve Exported Candidates), WIBIL may receive and store:</p>
                <ul>
                  <li>Limited LinkedIn stub profile fields returned by LinkedIn’s exported-candidates API, such as name, headline, location, current employer names, profile URL, and member identifier.</li>
                  <li>Export metadata such as recruiter notes, requester name, export time, organization identifier, and associated requisition or pipeline identifiers.</li>
                  <li>Push-notification identifiers (request IDs) sent to WIBIL’s registered callback URL when a Recruiter exports a candidate.</li>
                  <li>Notices that an exported member later closed their LinkedIn account, when WIBIL queries LinkedIn’s purged-exported-candidates API so records can be flagged.</li>
                </ul>
                <p className="mb-0">
                  LinkedIn export data is collected only after a Recruiter action or an authorized API call. WIBIL
                  does not use LinkedIn scraping to build candidate lists. InMail stub-profile contact details are
                  processed only if that LinkedIn feature is enabled and the member consents through LinkedIn.
                </p>
              </section>

              <section id="indeed-integration" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Indeed Integration</h2>
                <p>
                  When a customer enables Indeed Partner integration, WIBIL may store Indeed OAuth client credentials
                  and access tokens needed to call Indeed’s official APIs, including GraphQL Retrieve Candidates
                  (<code>fetchAssets</code>) for Indeed Apply applicants associated with an authorized employer.
                </p>
                <p>
                  WIBIL may also receive candidate payloads posted to a customer webhook URL, and may store Indeed
                  employer identifiers returned during OAuth. Indeed access tokens can be refreshed using Indeed’s
                  token endpoint when a refresh token is available.
                </p>
                <p className="mb-0">
                  WIBIL does not provide a public Indeed resume-search scrape. Dummy or invented Indeed profiles are
                  not used as a substitute for live Partner API results.
                </p>
              </section>

              <section id="how-we-use" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>How We Use Information</h2>
                <p>We use information to:</p>
                <ul className="mb-0">
                  <li>Create and authenticate accounts, verify email via OTP, and reset passwords.</li>
                  <li>Provide ATS and hiring features, including candidate matching, Opportunity Hub sourcing, and pipeline tracking.</li>
                  <li>Perform verification checks you request, such as GST lookup, EPFO/UAN checks, mobile OTP, and identity or photo comparison where those product flows are used.</li>
                  <li>Connect authorized LinkedIn RSC and Indeed Partner integrations and display exported or synced applicant data to the recruiting customer.</li>
                  <li>Send transactional email (OTP and similar service messages) and recruiter shortlist messages you initiate.</li>
                  <li>Remember sign-in state and display theme (light or dark) according to your preference.</li>
                  <li>Maintain consent and audit records used by Privacy &amp; Data Controls, including erasure requests.</li>
                </ul>
              </section>

              <section id="data-sharing" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Data Sharing and Disclosure</h2>
                <p>We share information only as needed to operate the Service:</p>
                <ul>
                  <li>With the employer or recruiter organization that uses WIBIL to evaluate or hire candidates, when you apply, are exported from LinkedIn Recruiter, or are otherwise added to that organization’s hiring workflow.</li>
                  <li>With LinkedIn or Indeed when a customer has connected those Partner integrations and the APIs require authentication or candidate-sync payloads.</li>
                  <li>With verification or messaging providers used by the product (for example GST verification, email delivery, or WhatsApp shortlist sending) when you or a recruiter use those features.</li>
                  <li>With hosting and database infrastructure that runs wibil.co.</li>
                </ul>
                <p className="mb-0">
                  We do not sell personal information. We may disclose information if required by applicable law or to
                  protect the security and integrity of the Service.
                </p>
              </section>

              <section id="data-security" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Data Security</h2>
                <p className="mb-0">
                  WIBIL is served over HTTPS. Account passwords are stored in hashed form by the authentication
                  service. Integration credentials and exported-candidate records are stored in application data files
                  that are blocked from direct web access by server configuration. Browser tokens are stored in
                  local storage and session storage on your device. No security measure is perfect; we do not claim
                  specific industry certifications or encryption algorithms beyond what is described here.
                </p>
              </section>

              <section id="data-retention" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Data Retention</h2>
                <p className="mb-0">
                  We keep account, profile, hiring, and integration records for as long as needed to provide the
                  Service and to honor a connected customer’s recruiting workflow. Indeed OAuth request identifiers
                  supplied by Indeed expire according to Indeed’s rules (authorization codes are short-lived). LinkedIn
                  export request IDs expire according to LinkedIn’s rules (typically 24 hours). We do not publish a
                  fixed multi-year retention schedule in this policy because the application does not implement a
                  single automatic purge calendar for all record types.
                </p>
              </section>

              <section id="data-deletion" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Data Deletion and User Requests</h2>
                <p>
                  Signed-in users can use Privacy &amp; Data Controls in the product to submit a data erasure request.
                  That request is processed through WIBIL’s consent service and is intended to support statutory
                  erasure rights under India’s Digital Personal Data Protection Act, 2023 for connected OAuth
                  accounts, public URL verification records, and consent history as implemented in that flow.
                </p>
                <p className="mb-0">
                  For LinkedIn RSC, WIBIL can flag exported candidates whose LinkedIn member accounts were later
                  closed, so customers can delete those records if they have no legal basis to keep them. Disconnecting
                  Indeed or LinkedIn from Opportunity Hub removes stored access tokens for that integration.
                </p>
              </section>

              <section id="cookies" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Cookies and Tracking Technologies</h2>
                <p className="mb-0">
                  WIBIL uses browser local storage and session storage to keep you signed in (<code>wibil_token</code>,
                  <code>wibil_user</code>) and to remember theme mode (<code>wibil_theme_mode</code>). The public site
                  loads Bootstrap and Google Fonts from third-party CDNs, which may set their own cookies according to
                  those providers’ policies. WIBIL does not operate a separate advertising-cookie program in the
                  current application.
                </p>
              </section>

              <section id="third-party-services" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Third-Party Services</h2>
                <p>Depending on features you use, WIBIL may send data to or receive data from:</p>
                <ul className="mb-0">
                  <li>LinkedIn APIs (Recruiter System Connect / exported candidates), when configured.</li>
                  <li>Indeed Partner APIs (OAuth and Retrieve Candidates / webhooks), when configured.</li>
                  <li>GitHub’s public API for recruiter candidate search results.</li>
                  <li>Email delivery (transactional OTP mail from WIBIL’s mailbox).</li>
                  <li>GST verification (SignCare) on employer registration when that check is used.</li>
                  <li>WhatsApp messaging for recruiter shortlists when that action is used.</li>
                  <li>Hosting, database, and CDN providers that serve wibil.co, Bootstrap, and fonts.</li>
                </ul>
              </section>

              <section id="user-rights" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>User Rights</h2>
                <p>
                  Depending on applicable law, including India’s DPDP Act, 2023, you may have the right to access,
                  correct, or request deletion of personal information we hold about you, and to withdraw consent
                  where processing is based on consent.
                </p>
                <p className="mb-0">
                  You can update much of your profile information while signed in. You can request erasure through
                  Privacy &amp; Data Controls where that control is available. You can also disconnect LinkedIn or
                  Indeed integrations from Opportunity Hub if you are the recruiting customer that connected them.
                </p>
              </section>

              <section id="children" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Children's Privacy</h2>
                <p className="mb-0">
                  WIBIL is a workforce and hiring platform intended for adults seeking or offering employment. We do
                  not knowingly collect personal information from children. If you believe a child has provided
                  information to WIBIL, contact us using the details below so we can review and delete the data where
                  appropriate.
                </p>
              </section>

              <section id="changes" className="mb-4 mb-md-5" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Changes to This Privacy Policy</h2>
                <p className="mb-0">
                  We may update this Privacy Policy as the Service changes. The effective date at the top of this page
                  will be revised when we do. Continued use of WIBIL after an update means you should review the
                  revised policy.
                </p>
              </section>

              <section id="contact" className="mb-0" style={{ scrollMarginTop: "5rem" }}>
                <h2 className="h4 fw-bold mb-3" style={{ fontFamily: fonts.display }}>Contact Us</h2>
                <p>
                  WIBIL — Workforce Information Bureau (India) Limited<br />
                  Operated with GoldPE.AI<br />
                  Website: <a href="https://wibil.co" className="text-primary">https://wibil.co</a>
                </p>
                <p className="mb-0">
                  For privacy requests, use Privacy &amp; Data Controls in your signed-in WIBIL account where
                  available, or contact us through the WIBIL website. Transactional mail from WIBIL (including OTP)
                  is sent from the platform’s noreply mailbox and is not monitored for privacy requests.
                </p>
              </section>
            </article>

            <p className="small text-secondary mt-4 mb-0">
              <Link to="/login" className="text-primary text-decoration-none fw-semibold">Back to sign in</Link>
            </p>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
